import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import Card from "components/card";
import { readToken } from "utils/session";
import { useAccountAccess } from "utils/access";
import { MdEdit, MdDelete, MdNotifications, MdClose } from "react-icons/md"; // Added icons
import { 
  GoogleMap, 
  useJsApiLoader, 
  DrawingManager, 
  Polygon 
} from "@react-google-maps/api";

const libraries = ["geometry", "drawing"]; 

const mapContainerStyle = {
  height: "500px",
  width: "100%",
};

const defaultCenter = { lat: 12.9716, lng: 77.5946 }; // Bangalore Default

const Geofence = () => {
  const access = useAccountAccess();
  const canEdit = !!access?.canEditGeofence;
  const [geofences, setGeofences] = useState([]);
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [coordinates, setCoordinates] = useState([]); 
  const [editingId, setEditingId] = useState(null);
  const [popupMessage, setPopupMessage] = useState({ text: "", type: "" });
  
  // NEW STATES FOR ALERTS
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [activeAlertGeo, setActiveAlertGeo] = useState(null);
  const [alertType, setAlertType] = useState("both"); // "entry", "exit", "both"
  const [alertChannels, setAlertChannels] = useState({
    sms: false,
    whatsapp: false,
    app: false,
    ivr: false
  });

  const polygonRef = useRef(null);
  const mapRef = useRef(null); // NEW: To hold the map instance for zooming

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    version: "3.64",
  });

  const userKey = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const axiosConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${userKey}`,
      "Content-Type": "application/json",
    },
  }), [userKey]);

  const fetchGeofences = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/geofences`, axiosConfig);
      setGeofences(response.data);
    } catch (error) {
      console.error("Error fetching geofences:", error);
    }
  }, [apiBaseUrl, axiosConfig]);

  useEffect(() => {
    fetchGeofences();
  }, [fetchGeofences]);

  const showPopup = (text, type = "success") => {
    setPopupMessage({ text, type });
    setTimeout(() => setPopupMessage({ text: "", type: "" }), 3000);
  };

  // --- MAP VIEW HANDLER (NEW) ---
  const handleViewGeofence = (geo) => {
    // Ensure map is loaded and we have coordinates
    if (mapRef.current && window.google && geo.coordinates.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Extend the bounds to include every point of the geofence
      geo.coordinates.forEach(coord => {
        bounds.extend(new window.google.maps.LatLng(coord.latitude, coord.longitude));
      });
      
      // Tell the map to fit those bounds (automatically handles zoom and center)
      mapRef.current.fitBounds(bounds);
    }
  };

  // --- ALERT MODAL HANDLERS (NEW) ---
  const openAlertModal = async (geo) => {
    setActiveAlertGeo(geo);
    setIsAlertModalOpen(true);
    
    try {
      // Make the GET request to pull existing subscriptions
      const response = await axios.get(`${apiBaseUrl}/api/geofences/${geo.id}/alerts`, axiosConfig);
      
      if (response.data.status === "success") {
        setAlertType(response.data.data.alertType);
        setAlertChannels(response.data.data.channels);
      }
    } catch (error) {
      console.error("Failed to load alert config, falling back to defaults.", error);
      setAlertType("both");
      setAlertChannels({ sms: false, whatsapp: false, app: false, ivr: false });
    }
  };

  const closeAlertModal = () => {
    setIsAlertModalOpen(false);
    setActiveAlertGeo(null);
  };

  const toggleChannel = (channel) => {
    setAlertChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
  };

  // 2. Updated handleSaveAlerts (Posts the new settings to the DB)
  const handleSaveAlerts = async () => {
    // Prevent saving if no channels are selected to avoid silent failures
    const hasAtLeastOneChannel = Object.values(alertChannels).some(val => val === true);
    if (!hasAtLeastOneChannel) {
      showPopup("Please select at least one notification channel (SMS, App, etc).", "error");
      return;
    }

    const payload = {
      alertType: alertType,
      channels: alertChannels
    };
    
    try {
      // THIS is the line that triggers the Network Tab activity!
      await axios.post(`${apiBaseUrl}/api/geofences/${activeAlertGeo.id}/alerts`, payload, axiosConfig);
      
      showPopup(`Alerts successfully configured for ${activeAlertGeo.name}`);
      closeAlertModal();
    } catch (error) {
      console.error("Failed to save alert config", error);
      showPopup("Failed to save alert configuration to server.", "error");
    }
  };

  // --- DRAWING HANDLERS ---
  const onPolygonComplete = useCallback((polygon) => {
    const path = polygon.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      coords.push({
        latitude: path.getAt(i).lat(),
        longitude: path.getAt(i).lng(),
      });
    }
    setCoordinates(coords);
    polygon.setMap(null); 
  }, []);

  const onRectangleComplete = useCallback((rectangle) => {
    const bounds = rectangle.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    const coords = [
      { latitude: ne.lat(), longitude: ne.lng() },
      { latitude: ne.lat(), longitude: sw.lng() },
      { latitude: sw.lat(), longitude: sw.lng() },
      { latitude: sw.lat(), longitude: ne.lng() }
    ];
    setCoordinates(coords);
    rectangle.setMap(null);
  }, []);

  const onEditPolygon = useCallback(() => {
    if (polygonRef.current) {
      const path = polygonRef.current.getPath();
      const coords = [];
      for (let i = 0; i < path.getLength(); i++) {
        coords.push({
          latitude: path.getAt(i).lat(),
          longitude: path.getAt(i).lng(),
        });
      }
      setCoordinates(coords);
    }
  }, []);

  // --- CRUD OPERATIONS ---
  const handleSave = async () => {
    // ... (Existing Handle Save remains exactly the same)
    if (!name || !identifier) return showPopup("Name and Identifier are required.", "error");
    if (coordinates.length < 3) return showPopup("Please draw a valid shape on the map.", "error");

    const payload = { name, identifier, coordinates };

    try {
      if (editingId) {
        await axios.put(`${apiBaseUrl}/api/geofences/${editingId}`, payload, axiosConfig);
        showPopup("Geofence updated successfully!");
      } else {
        await axios.post(`${apiBaseUrl}/api/geofences`, payload, axiosConfig);
        showPopup("Geofence created successfully!");
      }
      resetForm();
      fetchGeofences();
    } catch (error) {
      showPopup(error.response?.data?.error || "Error saving geofence", "error");
    }
  };

  const handleDelete = async (id) => {
    // ... (Existing Handle Delete remains exactly the same)
    if (window.confirm("Are you sure you want to delete this geofence?")) {
      try {
        await axios.delete(`${apiBaseUrl}/api/geofences/${id}`, axiosConfig);
        showPopup("Geofence deleted successfully!");
        if (editingId === id) resetForm();
        fetchGeofences();
      } catch (error) {
        showPopup("Failed to delete geofence", "error");
      }
    }
  };

  const handleEditSetup = (geo) => {
    setEditingId(geo.id);
    setName(geo.name);
    setIdentifier(geo.identifier);
    setCoordinates(geo.coordinates);
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setIdentifier("");
    setCoordinates([]);
  };

  return (
    <div className="mt-5 grid h-full grid-cols-1 gap-5 md:grid-cols-3">
      
      {/* LEFT COLUMN: Form & List */}
      <div className="flex flex-col gap-5 md:col-span-1">
        {canEdit ? (
        <Card extra="p-5 flex flex-col gap-4">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            {editingId ? "Edit Geofence" : "Create Geofence"}
          </h4>
          
          <div>
            <label className="text-sm text-gray-600 dark:text-white">Identifier</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={editingId !== null} 
              className="mt-1 flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 dark:text-white">Geofence Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
            />
          </div>

          <div className="mt-2 flex gap-2">
            <button onClick={handleSave} className="w-full rounded-xl bg-brand-500 py-3 text-base font-medium text-white transition hover:bg-brand-600 active:bg-brand-700">
              {editingId ? "Update" : "Save"} Geofence
            </button>
            {(editingId || coordinates.length > 0) && (
              <button onClick={resetForm} className="w-full rounded-xl bg-gray-200 py-3 text-base font-medium text-navy-700 transition hover:bg-gray-300">
                Cancel / Clear
              </button>
            )}
          </div>

          {popupMessage.text && (
            <div className={`rounded-md p-3 text-sm font-bold ${popupMessage.type === "error" ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-600 border border-green-200"}`}>
              {popupMessage.text}
            </div>
          )}
        </Card>
        ) : (
        <Card extra="p-5">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">Geofences</h4>
          <p className="mt-2 text-sm text-gray-600">
            You can view geofences and set alerts. Creating or changing a geofence is limited to the customer admin and zone accounts.
          </p>
          {popupMessage.text && (
            <div className={`mt-3 rounded-md p-3 text-sm font-bold ${popupMessage.type === "error" ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-600 border border-green-200"}`}>
              {popupMessage.text}
            </div>
          )}
        </Card>
        )}

        <Card extra="max-h-[500px] overflow-y-auto p-5">
          <h4 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">Saved Geofences</h4>
          {geofences.length === 0 ? (
            <p className="text-sm text-gray-500">No geofences found.</p>
          ) : (
            geofences.map((geo) => (
              <div key={geo.id} className="mb-3 flex items-center justify-between rounded-xl bg-lightPrimary p-3 dark:bg-navy-900 transition hover:bg-gray-50 dark:hover:bg-navy-800">
                {/* Clickable Area for Centering Map */}
                <div 
                  className="cursor-pointer flex-1" 
                  onClick={() => handleViewGeofence(geo)}
                  title="Click to view on map"
                >
                  <p className="text-base font-bold text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-400">{geo.name}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{geo.identifier}</p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button onClick={() => openAlertModal(geo)} className="text-xl text-brand-500 hover:text-brand-600" title="Configure Alerts">
                    <MdNotifications />
                  </button>
                  {canEdit && (
                    <button onClick={() => handleEditSetup(geo)} className="text-xl text-blue-500 hover:text-blue-700" title="Edit Geofence">
                      <MdEdit />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => handleDelete(geo.id)} className="text-xl text-red-500 hover:text-red-700" title="Delete Geofence">
                      <MdDelete />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </Card>
      </div>

      {/* RIGHT COLUMN: Google Map */}
      <Card extra="overflow-hidden rounded-[20px] p-2 md:col-span-2">
        <div className="relative z-0 h-full w-full">
          {!isLoaded ? (
            <div className="flex h-full items-center justify-center">Loading map...</div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={10}
              onLoad={(map) => { mapRef.current = map; }} // Set map reference
            >
              {/* Drawing Toolbar (Hides when a shape is drawn/edited) */}
              {canEdit && coordinates.length === 0 && (
                <DrawingManager
                  onPolygonComplete={onPolygonComplete}
                  onRectangleComplete={onRectangleComplete}
                  options={{
                    drawingControl: true,
                    drawingControlOptions: {
                      position: window.google.maps.ControlPosition.TOP_CENTER,
                      drawingModes: ["polygon", "rectangle"],
                    },
                    polygonOptions: { fillColor: "#4285F4", fillOpacity: 0.4, strokeWeight: 2 },
                    rectangleOptions: { fillColor: "#4285F4", fillOpacity: 0.4, strokeWeight: 2 },
                  }}
                />
              )}

              {/* Actively Drawn / Edited Shape */}
              {canEdit && coordinates.length > 0 && (
                <Polygon
                  path={coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }))}
                  options={{
                    fillColor: "#4285F4",
                    fillOpacity: 0.4,
                    strokeColor: "#4285F4",
                    strokeWeight: 2,
                    editable: true,
                    draggable: true,
                  }}
                  onLoad={(polygon) => { polygonRef.current = polygon; }}
                  onMouseUp={onEditPolygon}
                  onDragEnd={onEditPolygon}
                />
              )}

              {/* Background Saved Geofences */}
              {geofences.map((geo) => {
                if (geo.id === editingId) return null;
                return (
                  <Polygon
                    key={geo.id}
                    path={geo.coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }))}
                    options={{
                      fillColor: "#808080",
                      fillOpacity: 0.2,
                      strokeColor: "#808080",
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                );
              })}
            </GoogleMap>
          )}
        </div>
      </Card>

      {/* ALERT CONFIGURATION MODAL */}
      {isAlertModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
          <Card extra="w-full max-w-md flex-col p-6 shadow-2xl relative">
            
            {/* Modal Header */}
            <div className="mb-4 flex items-center justify-between border-b pb-4 dark:border-navy-600">
              <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                Alert Setup: {activeAlertGeo?.name}
              </h2>
              <button
                onClick={closeAlertModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-navy-700 dark:hover:bg-navy-600"
              >
                <MdClose className="h-5 w-5 text-gray-600 dark:text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col gap-6">
              
              {/* Type of Alert */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-navy-700 dark:text-white">Type of Alert</label>
                <select
                  value={alertType}
                  onChange={(e) => setAlertType(e.target.value)}
                  className="w-full rounded-md border border-gray-200 bg-white p-2.5 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                >
                  <option value="entry">Entry Only</option>
                  <option value="exit">Exit Only</option>
                  <option value="both">Both (Entry & Exit)</option>
                </select>
              </div>

              {/* Notification Channels */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-navy-700 dark:text-white">Notification Channels</label>
                <p className="text-xs text-gray-500">Select multiple options.</p>
                
                <div className="mt-2 flex flex-col gap-3">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={alertChannels.sms} onChange={() => toggleChannel('sms')} className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700 dark:text-white">SMS</span>
                  </label>
                  
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={alertChannels.whatsapp} onChange={() => toggleChannel('whatsapp')} className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700 dark:text-white">WhatsApp</span>
                  </label>
                  
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={alertChannels.app} onChange={() => toggleChannel('app')} className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700 dark:text-white">App Notification</span>
                  </label>
                  
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={alertChannels.ivr} onChange={() => toggleChannel('ivr')} className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700 dark:text-white">IVR Call</span>
                  </label>
                </div>
              </div>

            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex gap-3 border-t pt-4 dark:border-navy-600">
              <button 
                onClick={handleSaveAlerts}
                className="w-full rounded-md bg-brand-500 py-2 text-sm font-medium text-white transition hover:bg-brand-600"
              >
                Save Alerts
              </button>
              <button 
                onClick={closeAlertModal}
                className="w-full rounded-md border border-gray-200 bg-white py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
              >
                Cancel
              </button>
            </div>

          </Card>
        </div>
      )}

    </div>
  );
};

export default Geofence;